import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IPhone } from './interface'
import { Button, Input } from 'antd'
import { IconsWrapper } from './styles'
import { modalPhone } from './mango-call'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import ru from 'react-phone-input-2/lang/ru.json'
import { ContactIcon, PhoneIcon, TelegramIcon, WhatsappIcon } from '../../assets/icons'
import { useDemoPrivacy } from '../../contexts/demo-privacy-context'
import { formatPhoneForDemo } from 'src/lib/demo-privacy'

const normalizePhone = (phone?: string | null): string => {
  if (typeof phone !== 'string') {
    return ''
  }
  return phone.replace(/[^\d]/g, '')
}

const formatWithPlus = (digits: string): string => (digits ? `+${digits}` : '')

function InputPhone({ value, onChange, UISchema }: IPhone) {
  const location = useLocation()
  const { hidePhones } = useDemoPrivacy()
  // Маскировка только на карточке клиента (/client-new/:id)
  const shouldMask = hidePhones && location.pathname.startsWith('/client-new/')
  const disabled = useMemo(
    () => Boolean(UISchema?.options?.disabled) || shouldMask,
    [UISchema, shouldMask],
  )
  const areaCodes = [
    '310',
    '311',
    '312',
    '313',
    '315',
    '318',
    '321',
    '324',
    '325',
    '326',
    '327',
    '336',
    '7172',
    '73622',
  ]
  const areaCodesWithBrackets = areaCodes.map(code => `(${code})`)
  const hideDashes = useMemo(() => Boolean(UISchema?.options?.hideDashes), [UISchema])
  const customer_id = useMemo(() => UISchema?.options?.customer_id, [UISchema])
  const [localValue, setLocalValue] = useState<string>(() => normalizePhone(value))

  useEffect(() => {
    const normalized = normalizePhone(value)
    setLocalValue(prev => (prev === normalized ? prev : normalized))
  }, [value])

  const getFormatLength = useCallback((country: any) => {
    const format = country?.['format']
    if (!format) {
      return null
    }
    return (format.match(/\./g) || []).length
  }, [])

  const handleValueChange = useCallback(
    (nextValue: string, country: any) => {
      if (shouldMask) {
        return
      }
      const digits = nextValue.replace(/[^\d]/g, '')
      setLocalValue(digits)

      const expectedLength = getFormatLength(country)

      if (digits.length === 0) {
        if (value) {
          onChange?.('')
        }
        return
      }

      if (expectedLength && digits.length === expectedLength) {
        const formatted = formatWithPlus(digits)
        if (formatted !== value) {
          onChange?.(formatted)
        }
      }
    },
    [getFormatLength, onChange, shouldMask, value],
  )

  const onCall = useCallback(() => {
    if (shouldMask) {
      return
    }
    const digits = normalizePhone(value)
    if (!digits) {
      console.warn('Некорректный номер телефона для звонка')
      return
    }
    modalPhone({ phone: formatWithPlus(digits) })
  }, [shouldMask, value])

  const onClient = useCallback(async () => {
    Object.assign(window.location, { href: `/client-new/${customer_id}` })
  }, [customer_id])

  const actionsDisabled = shouldMask || !Boolean(value)

  return (
    <div
      style={{ display: 'flex', width: 'max-content', gap: '2px' }}
      className="input_phone_number"
    >
      {!hideDashes && customer_id && (
        <Button
          type="link"
          onClick={onClient}
          style={{ padding: '0', display: 'flex' }}
          disabled={false}
          icon={<ContactIcon />}
        />
      )}
      {shouldMask ? (
        <Input
          value={value ? formatPhoneForDemo(value) : ''}
          disabled
          style={{ width: 180, borderRadius: 6, height: 32 }}
        />
      ) : (
        <PhoneInput
          enableSearch={true}
          searchPlaceholder={'Поиск'}
          localization={ru}
          country={'ru'}
          onlyCountries={['ru', 'kz', 'by', 'tj', 'uz', 'am', 'kg', 'az', 'md', 'tm']}
          value={localValue}
          disabled={disabled}
          containerStyle={{ width: 'auto' }}
          inputStyle={{ borderRadius: '6px', height: '32px' }}
          areaCodes={areaCodesWithBrackets}
          masks={{ kz: '(...) ..-..-..' }}
          isValid={(number, country) => {
            if (number.length < 3) {
              return true
            }
            const expectedLength = getFormatLength(country)
            if (!expectedLength) {
              return true
            }
            return number.length <= expectedLength
          }}
          onChange={handleValueChange}
        />
      )}
      <IconsWrapper>
        <Button
          type="link"
          href={shouldMask ? undefined : `https://t.me/+${value?.replace(/\D/g, '')}`}
          target={shouldMask ? undefined : '_blank'}
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={actionsDisabled}
          icon={<TelegramIcon disabled={actionsDisabled} />}
        />
        <Button
          type="link"
          href={shouldMask ? undefined : `https://wa.me/+${value?.replace(/\D/g, '')}`}
          target={shouldMask ? undefined : '_blank'}
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={actionsDisabled}
          icon={<WhatsappIcon disabled={actionsDisabled} />}
        />
        <Button
          type="link"
          onClick={onCall}
          style={{ padding: '0', display: 'flex', overflow: 'hidden', border: 'none' }}
          disabled={actionsDisabled}
          icon={<PhoneIcon disabled={actionsDisabled} />}
        />
      </IconsWrapper>
    </div>
  )
}

export default InputPhone
